/**
 * Session Manager Component
 * Handles session expiration by silently redirecting to login.
 */

import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuthApi';
import apiClient from '@/lib/api/client';

interface SessionManagerProps {
  children: React.ReactNode;
}

export const SessionManager: React.FC<SessionManagerProps> = ({ children }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const handlingRef = useRef(false);

  useEffect(() => {
    const handleSessionExpired = async () => {
      // Prevent multiple simultaneous redirects
      if (handlingRef.current) return;
      handlingRef.current = true;

      try {
        await logout();
      } catch {
        // Ignore logout errors — session is already gone
      } finally {
        navigate('/login?expired=true', { replace: true });
      }
    };

    apiClient.setSessionExpiredCallback(handleSessionExpired);

    return () => {
      handlingRef.current = false;
    };
  }, [logout, navigate]);

  return <>{children}</>;
};

export default SessionManager;
